import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// ============================================
// snake_case → camelCase deep transformer
// ============================================
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase());
}

function transformKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        snakeToCamel(key),
        transformKeys(value),
      ]),
    );
  }
  return obj;
}

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle errors & refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Convert all snake_case keys to camelCase recursively
    if (response.data && typeof response.data === 'object') {
      response.data = transformKeys(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl: string = originalRequest?.url ?? '';

    // Não interceptar 401 em endpoints de autenticação (login/register)
    // Nesses casos o erro deve fluir normalmente para o componente
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register');

    // Handle 401 em endpoints protegidos — tenta refresh token
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed - logout
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    // Mostrar toast de erro para todos os casos (exceto 401 em rotas protegidas)
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Ocorreu um erro inesperado';

    const shouldShowToast = !(
      error.response?.status === 401 && !isAuthEndpoint
    );
    if (shouldShowToast) {
      toast.error(Array.isArray(message) ? message.join(', ') : message);
    }

    return Promise.reject(error);
  },
);

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data).then((r) => r.data.data),

  register: (data: { name: string; email: string; password: string }) =>
    api.post('/auth/register', data).then((r) => r.data.data),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }).then((r) => r.data.data),

  logout: () => api.post('/auth/logout').then((r) => r.data.data),

  me: () => api.get('/auth/me').then((r) => r.data.data),

  requestRegistration: (email: string) =>
    api.post('/auth/request-registration', { email }).then((r) => r.data.data),

  validateRegistrationToken: (token: string) =>
    api.get(`/auth/validate-registration-token/${token}`).then((r) => r.data.data),

  completeRegistration: (data: Record<string, unknown>) =>
    api.post('/auth/complete-registration', data).then((r) => r.data.data),
};

// ============================================
// Assets API
// ============================================
export const assetsApi = {
  getAll: (params?: { type?: string; sector?: string }) =>
    api.get('/assets', { params }).then((r) => r.data.data),

  getById: (id: string) =>
    api.get(`/assets/${id}`).then((r) => r.data.data),

  create: (data: unknown) =>
    api.post('/assets', data).then((r) => r.data.data),

  update: (id: string, data: unknown) =>
    api.put(`/assets/${id}`, data).then((r) => r.data.data),

  remove: (id: string) =>
    api.delete(`/assets/${id}`).then((r) => r.data.data),

  getPerformance: (id: string) =>
    api.get(`/assets/${id}/performance`).then((r) => r.data.data),

  getHistory: (id: string) =>
    api.get(`/assets/${id}/history`).then((r) => r.data.data),

  addTransaction: (id: string, data: unknown) =>
    api.post(`/assets/${id}/transactions`, data).then((r) => r.data.data),

  refreshPrices: () =>
    api.post('/assets/refresh-prices').then((r) => r.data.data),
};

// ============================================
// Portfolio API
// ============================================
export const portfolioApi = {
  getSummary: () =>
    api.get('/portfolio/summary').then((r) => r.data.data),

  getAllocation: () =>
    api.get('/portfolio/allocation').then((r) => r.data.data),

  getPerformance: () =>
    api.get('/portfolio/performance').then((r) => r.data.data),

  getValue: () =>
    api.get('/portfolio/value').then((r) => r.data.data),

  getHistory: () =>
    api.get('/portfolio/history').then((r) => r.data.data),
};

// ============================================
// Dividends API
// ============================================
export const dividendsApi = {
  getAll: (params?: { year?: number }) =>
    api.get('/dividends', { params }).then((r) => r.data.data),

  getCalendar: (year?: number) =>
    api.get('/dividends/calendar', { params: { year } }).then((r) => r.data.data),

  getProjection: (months?: number) =>
    api.get('/dividends/projection', { params: { months } }).then((r) => r.data.data),

  getTotals: () =>
    api.get('/dividends/totals').then((r) => r.data.data),

  getByAsset: (assetId: string) =>
    api.get(`/dividends/${assetId}`).then((r) => r.data.data),

  create: (data: unknown) =>
    api.post('/dividends', data).then((r) => r.data.data),
};

// ============================================
// Goals API
// ============================================
export const goalsApi = {
  getAll: () =>
    api.get('/goals').then((r) => r.data.data),

  getProgress: () =>
    api.get('/goals/progress').then((r) => r.data.data),

  create: (data: unknown) =>
    api.post('/goals', data).then((r) => r.data.data),

  updateAll: (data: { goals: Array<{ assetType: string; targetPercentage: number }> }) =>
    api.put('/goals', data).then((r) => r.data.data),

  remove: (id: string) =>
    api.delete(`/goals/${id}`).then((r) => r.data.data),
};

// ============================================
// Alerts API
// ============================================
export const alertsApi = {
  getAll: () =>
    api.get('/alerts').then((r) => r.data.data),

  create: (data: unknown) =>
    api.post('/alerts', data).then((r) => r.data.data),

  update: (id: string, data: unknown) =>
    api.put(`/alerts/${id}`, data).then((r) => r.data.data),

  remove: (id: string) =>
    api.delete(`/alerts/${id}`).then((r) => r.data.data),

  check: () =>
    api.get('/alerts/check').then((r) => r.data.data),
};

// ============================================
// Market Data API
// ============================================
export const marketApi = {
  getQuote: (symbol: string) =>
    api.get(`/market/quote/${symbol}`).then((r) => r.data.data),

  getHistory: (symbol: string, from?: string, to?: string) =>
    api.get(`/market/history/${symbol}`, { params: { from, to } }).then((r) => r.data.data),

  getCrypto: (coinId: string) =>
    api.get(`/market/crypto/${coinId}`).then((r) => r.data.data),
};

// ============================================
// Imports API
// ============================================
export const importsApi = {
  preview: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/imports/b3/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data.data);
  },

  confirm: (transactions: unknown[]) =>
    api.post('/imports/b3/confirm', { transactions }).then((r) => r.data.data),
};

export default api;
